import { Box, VStack, Input, Text, Image, Alert, AlertIcon, Spinner, Button, useToast, Tabs, TabList, TabPanels, TabPanel, Tab, Progress, SimpleGrid, IconButton, HStack, Flex, Card, CardBody, Slider, SliderTrack, SliderFilledTrack, SliderThumb, Switch } from '@chakra-ui/react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { Header, InsightLogoPlaceholder } from '../Header';
import { SearchResults } from '../components/SearchResults/SearchResults';
import { FeatureEnhance } from '../components/FeatureEnhance/FeatureEnhance';
import { useNavigate } from 'react-router-dom';
import { FaHome, FaTimes } from 'react-icons/fa';

const API_URL = 'https://slack.westchesterrtc.com/insight';
type EyeHole = { cx: number; cy: number; rx: number; ry: number; rotation: number };

// Custom smaller logo component for Face-to-Face section
const SmallInsightLogoPlaceholder = ({ isDragging }: { isDragging: boolean }) => {
  return (
    <Box
      position="relative"
      width="156px"
      height="156px"
      mb="20px"
    >
      <Image
        src="/images/RTC-blue_logo.png"
        alt="Drop Zone"
        boxSize="156px"
        position="relative"
        opacity={isDragging ? 1 : 0.7}
        transition="all 0.3s"
        _hover={{
          opacity: 1
        }}
      />
    </Box>
  );
};

const EllipseEditor = ({
  imageUrl,
  ellipses,
  onChange,
}: {
  imageUrl: string;
  ellipses: EyeHole[];
  onChange: (next: EyeHole[]) => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const dragStateRef = useRef<{
    mode: 'move' | 'resizeX' | 'resizeY' | null;
    idx: number;
    startX: number;
    startY: number;
  }>({ mode: null, idx: -1, startX: 0, startY: 0 });
  const [activeEdit, setActiveEdit] = useState<{ mode: 'move' | 'resizeX' | 'resizeY' | null; idx: number }>({
    mode: null,
    idx: -1,
  });
  const displaySize = 224;
  const scale = displaySize / 112;

  const hitTest = (x: number, y: number) => {
    for (let i = ellipses.length - 1; i >= 0; i--) {
      const e = ellipses[i];
      const hx = e.cx + e.rx;
      const hy = e.cy + e.ry;
      if (Math.hypot(x - hx, y - e.cy) < 4) return { mode: 'resizeX' as const, idx: i };
      if (Math.hypot(x - e.cx, y - hy) < 4) return { mode: 'resizeY' as const, idx: i };
      const nx = (x - e.cx) / Math.max(1e-6, e.rx);
      const ny = (y - e.cy) / Math.max(1e-6, e.ry);
      if (nx * nx + ny * ny <= 1.0) return { mode: 'move' as const, idx: i };
    }
    return { mode: null, idx: -1 };
  };

  const toCanvasCoords = (
    clientX: number,
    clientY: number,
    canvas: HTMLCanvasElement,
  ) => {
    const rect = canvas.getBoundingClientRect();
    const px = ((clientX - rect.left) / rect.width) * 112;
    const py = ((clientY - rect.top) / rect.height) * 112;
    return { x: Math.max(0, Math.min(111, px)), y: Math.max(0, Math.min(111, py)) };
  };

  const onPointerDown = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    evt.preventDefault();
    const canvas = evt.currentTarget;
    canvas.setPointerCapture(evt.pointerId);
    const { x, y } = toCanvasCoords(evt.clientX, evt.clientY, canvas);
    const hit = hitTest(x, y);
    dragStateRef.current = { mode: hit.mode, idx: hit.idx, startX: x, startY: y };
    setActiveEdit({ mode: hit.mode, idx: hit.idx });
  };

  const onPointerUp = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    evt.preventDefault();
    dragStateRef.current = { mode: null, idx: -1, startX: 0, startY: 0 };
    setActiveEdit({ mode: null, idx: -1 });
  };

  const onPointerMove = (evt: React.PointerEvent<HTMLCanvasElement>) => {
    evt.preventDefault();
    const drag = dragStateRef.current;
    if (!drag.mode || drag.idx < 0) return;
    const { x, y } = toCanvasCoords(evt.clientX, evt.clientY, evt.currentTarget);
    const dx = x - drag.startX;
    const dy = y - drag.startY;
    const next = ellipses.map((e) => ({ ...e }));
    const e = next[drag.idx];

    if (drag.mode === 'move') {
      e.cx = Math.max(0, Math.min(111, e.cx + dx));
      e.cy = Math.max(0, Math.min(111, e.cy + dy));
    } else if (drag.mode === 'resizeX') {
      e.rx = Math.max(2, Math.min(56, Math.abs(x - e.cx)));
    } else if (drag.mode === 'resizeY') {
      e.ry = Math.max(2, Math.min(56, Math.abs(y - e.cy)));
    }
    onChange(next);
    dragStateRef.current.startX = x;
    dragStateRef.current.startY = y;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imageUrl) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new window.Image();
    img.onload = () => {
      ctx.clearRect(0, 0, displaySize, displaySize);
      ctx.drawImage(img, 0, 0, displaySize, displaySize);
      ellipses.forEach((e, idx) => {
        const color = idx === 0 ? '#4FD1C5' : '#F6AD55';
        const isActive = activeEdit.idx === idx;
        ctx.strokeStyle = color;
        ctx.lineWidth = isActive ? 3 : 2;
        ctx.beginPath();
        ctx.ellipse(e.cx * scale, e.cy * scale, e.rx * scale, e.ry * scale, e.rotation, 0, Math.PI * 2);
        ctx.stroke();

        ctx.fillStyle = activeEdit.mode === 'resizeX' && isActive ? '#FFFFFF' : color;
        ctx.beginPath();
        ctx.arc((e.cx + e.rx) * scale, e.cy * scale, isActive ? 5 : 4, 0, Math.PI * 2);
        ctx.fill();
        if (activeEdit.mode === 'resizeX' && isActive) {
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.stroke();
        }

        ctx.fillStyle = activeEdit.mode === 'resizeY' && isActive ? '#FFFFFF' : color;
        ctx.beginPath();
        ctx.arc(e.cx * scale, (e.cy + e.ry) * scale, isActive ? 5 : 4, 0, Math.PI * 2);
        ctx.fill();
        if (activeEdit.mode === 'resizeY' && isActive) {
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });
    };
    img.src = imageUrl;
  }, [imageUrl, ellipses, activeEdit]);

  return (
    <canvas
      ref={canvasRef}
      width={displaySize}
      height={displaySize}
      style={{ width: `${displaySize}px`, height: `${displaySize}px`, border: '1px solid #4A5568', borderRadius: '6px', cursor: 'crosshair', touchAction: 'none' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onPointerLeave={onPointerUp}
    />
  );
};

function InSight() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedFaceUrl, setSubmittedFaceUrl] = useState<string | null>(null);
  const [searchStage, setSearchStage] = useState<string>('');
  const [searchProgress, setSearchProgress] = useState<number>(0);
  
  // Aggregate mode state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isAggregateDragging, setIsAggregateDragging] = useState(false);
  const [isAggregateLoading, setIsAggregateLoading] = useState(false);
  const [aggregateError, setAggregateError] = useState<string | null>(null);
  const [aggregateSearchStage, setAggregateSearchStage] = useState<string>('');
  const [aggregateSearchProgress, setAggregateSearchProgress] = useState<number>(0);
  
  // Face-to-Face matching state
  const [firstImage, setFirstImage] = useState<File | null>(null);
  const [secondImage, setSecondImage] = useState<File | null>(null);
  const [firstPreviewUrl, setFirstPreviewUrl] = useState<string | null>(null);
  const [secondPreviewUrl, setSecondPreviewUrl] = useState<string | null>(null);
  const [isFaceToFaceLoading, setIsFaceToFaceLoading] = useState(false);
  const [isEyesOnlyLoading, setIsEyesOnlyLoading] = useState(false);
  const [faceToFaceError, setFaceToFaceError] = useState<string | null>(null);
  const [similarityResult, setSimilarityResult] = useState<number | null>(null);
  const [eyesOnlySimilarityResult, setEyesOnlySimilarityResult] = useState<number | null>(null);
  const [periocularFbrSimilarityResult, setPeriocularFbrSimilarityResult] = useState<number | null>(null);
  const [periocularFbrRawSimilarityResult, setPeriocularFbrRawSimilarityResult] = useState<number | null>(null);
  const [isPeriocularFbrLoading, setIsPeriocularFbrLoading] = useState(false);
  const [eyesOnlyRawSimilarity, setEyesOnlyRawSimilarity] = useState<number | null>(null);
  const [eyesOnlyBaseline, setEyesOnlyBaseline] = useState<number | null>(null);
  const [eyesOnlyModelBreakdown, setEyesOnlyModelBreakdown] = useState<Array<{ model: string; score: number }>>([]);
  const [firstMaskedEyesUrl, setFirstMaskedEyesUrl] = useState<string | null>(null);
  const [secondMaskedEyesUrl, setSecondMaskedEyesUrl] = useState<string | null>(null);
  const [alignedSourceUrl, setAlignedSourceUrl] = useState<string | null>(null);
  const [alignedTargetUrl, setAlignedTargetUrl] = useState<string | null>(null);
  const [fbrSourceLeftUrl, setFbrSourceLeftUrl] = useState<string | null>(null);
  const [fbrSourceRightUrl, setFbrSourceRightUrl] = useState<string | null>(null);
  const [fbrTargetLeftUrl, setFbrTargetLeftUrl] = useState<string | null>(null);
  const [fbrTargetRightUrl, setFbrTargetRightUrl] = useState<string | null>(null);
  const [sourceEyeHoles, setSourceEyeHoles] = useState<EyeHole[]>([]);
  const [targetEyeHoles, setTargetEyeHoles] = useState<EyeHole[]>([]);
  const [useSkiMaskMode, setUseSkiMaskMode] = useState(false);
  const [roiSource, setRoiSource] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [roiTarget, setRoiTarget] = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null);
  const [roiWScale, setRoiWScale] = useState(1.1);
  const [roiHScale, setRoiHScale] = useState(0.45);
  const [roiXOffset, setRoiXOffset] = useState(0);
  const [roiYOffset, setRoiYOffset] = useState(-0.03);
  const [modelBreakdown, setModelBreakdown] = useState<Array<{ model: string; score: number }>>([]);
  const [isFirstDragging, setIsFirstDragging] = useState(false);
  const [isSecondDragging, setIsSecondDragging] = useState(false);
  
  const navigate = useNavigate();
  const toast = useToast();

  // Define search stages
  const searchStages = [
    "Detecting faces in image...",
    "Generating Buffalo_L embedding...",
    "Generating Buffalo_S embedding...",
    "Generating AntelopeV2 embedding...",
    "Generating AdaFace embedding...",
    "Generating MagFace embedding...",
    "Querying database for matches...",
    "Filtering and ranking results...",
    "Preparing response..."
  ];

  // Define aggregate search stages
  const aggregateSearchStages = [
    "Processing multiple images...",
    "Extracting embeddings from all images...",
    "Aggregating embeddings using quality-weighted averaging...",
    "Normalizing aggregated vectors...",
    "Querying database for matches...",
    "Filtering and ranking results...",
    "Preparing response..."
  ];

  // Function to simulate progress through stages
  const simulateSearchProgress = () => {
    let currentStage = 0;
    setSearchStage(searchStages[0]);
    setSearchProgress(5);

    const interval = setInterval(() => {
      if (currentStage < searchStages.length - 1) {
        currentStage++;
        setSearchStage(searchStages[currentStage]);
        setSearchProgress(Math.min(95, (currentStage / (searchStages.length - 1)) * 100));
      } else {
        clearInterval(interval);
      }
    }, 800); // Update every 800ms

    return interval;
  };

  // Function to simulate aggregate search progress
  const simulateAggregateSearchProgress = () => {
    let currentStage = 0;
    setAggregateSearchStage(aggregateSearchStages[0]);
    setAggregateSearchProgress(5);

    const interval = setInterval(() => {
      if (currentStage < aggregateSearchStages.length - 1) {
        currentStage++;
        setAggregateSearchStage(aggregateSearchStages[currentStage]);
        setAggregateSearchProgress(Math.min(95, (currentStage / (aggregateSearchStages.length - 1)) * 100));
      } else {
        clearInterval(interval);
      }
    }, 1000); // Update every 1000ms for aggregate (slower)

    return interval;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
      handleSubmit(file);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setSelectedFile(file);
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
      handleSubmit(file);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  // Aggregate file handlers
  const handleAggregateFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      addFilesToAggregate(newFiles);
    }
  };

  const handleAggregateDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsAggregateDragging(false);
    setAggregateError(null);

    if (e.dataTransfer.files) {
      const newFiles = Array.from(e.dataTransfer.files);
      addFilesToAggregate(newFiles);
    }
  }, []);

  const handleAggregateDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsAggregateDragging(true);
  }, []);

  const handleAggregateDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsAggregateDragging(false);
  }, []);

  const addFilesToAggregate = (newFiles: File[]) => {
    const validFiles = newFiles.filter(file => file.type.startsWith('image/'));
    const totalFiles = selectedFiles.length + validFiles.length;
    
    if (totalFiles > 10) {
      setAggregateError(`Maximum 10 files allowed. You can add ${10 - selectedFiles.length} more files.`);
      return;
    }

    const newUrls = validFiles.map(file => URL.createObjectURL(file));
    setSelectedFiles(prev => [...prev, ...validFiles]);
    setPreviewUrls(prev => [...prev, ...newUrls]);
    setAggregateError(null);
  };

  const removeFileFromAggregate = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      URL.revokeObjectURL(prev[index]); // Clean up the URL
      return prev.filter((_, i) => i !== index);
    });
  };

  // Face-to-Face handlers
  const handleFaceToFaceFileChange = (e: React.ChangeEvent<HTMLInputElement>, isSecond = false) => {
    const file = e.target.files?.[0];
    if (file) {
      if (isSecond) {
        setSecondImage(file);
        setSecondPreviewUrl(URL.createObjectURL(file));
      } else {
        setFirstImage(file);
        setFirstPreviewUrl(URL.createObjectURL(file));
      }
      setSimilarityResult(null);
      setEyesOnlySimilarityResult(null);
      setPeriocularFbrSimilarityResult(null);
      setPeriocularFbrRawSimilarityResult(null);
      setEyesOnlyRawSimilarity(null);
      setEyesOnlyBaseline(null);
      setEyesOnlyModelBreakdown([]);
      setFirstMaskedEyesUrl(null);
      setSecondMaskedEyesUrl(null);
      setAlignedSourceUrl(null);
      setAlignedTargetUrl(null);
      setFbrSourceLeftUrl(null);
      setFbrSourceRightUrl(null);
      setFbrTargetLeftUrl(null);
      setFbrTargetRightUrl(null);
      setSourceEyeHoles([]);
      setTargetEyeHoles([]);
      setRoiSource(null);
      setRoiTarget(null);
      setModelBreakdown([]);
      setFaceToFaceError(null);
    }
  };

  const handleFaceToFaceDrop = useCallback((e: React.DragEvent, isSecond = false) => {
    e.preventDefault();
    if (isSecond) {
      setIsSecondDragging(false);
    } else {
      setIsFirstDragging(false);
    }
    setFaceToFaceError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isSecond) {
        setSecondImage(file);
        setSecondPreviewUrl(URL.createObjectURL(file));
      } else {
        setFirstImage(file);
        setFirstPreviewUrl(URL.createObjectURL(file));
      }
      setSimilarityResult(null);
      setEyesOnlySimilarityResult(null);
      setPeriocularFbrSimilarityResult(null);
      setPeriocularFbrRawSimilarityResult(null);
      setEyesOnlyRawSimilarity(null);
      setEyesOnlyBaseline(null);
      setEyesOnlyModelBreakdown([]);
      setFirstMaskedEyesUrl(null);
      setSecondMaskedEyesUrl(null);
      setAlignedSourceUrl(null);
      setAlignedTargetUrl(null);
      setFbrSourceLeftUrl(null);
      setFbrSourceRightUrl(null);
      setFbrTargetLeftUrl(null);
      setFbrTargetRightUrl(null);
      setSourceEyeHoles([]);
      setTargetEyeHoles([]);
      setRoiSource(null);
      setRoiTarget(null);
      setModelBreakdown([]);
    }
  }, []);

  const handleFaceToFaceDragOver = useCallback((e: React.DragEvent, isSecond = false) => {
    e.preventDefault();
    if (isSecond) {
      setIsSecondDragging(true);
    } else {
      setIsFirstDragging(true);
    }
  }, []);

  const handleFaceToFaceDragLeave = useCallback((e: React.DragEvent, isSecond = false) => {
    e.preventDefault();
    if (isSecond) {
      setIsSecondDragging(false);
    } else {
      setIsFirstDragging(false);
    }
  }, []);

  const handleAggregateSubmit = async () => {
    if (selectedFiles.length === 0) {
      setAggregateError("Please select at least one image");
      return;
    }

    // Create collage from selected images
    const collageBlob = await createImageCollage(selectedFiles);
    if (!collageBlob) {
      setAggregateError("Failed to create image collage");
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('files', file);
    });
    
    // Add the collage as a reference image
    formData.append('collage', collageBlob, 'aggregate_collage.jpg');
    
    // Generate a more reliable unique identifier
    const timestamp = new Date().getTime();
    const randomStr = Math.random().toString(36).substring(2, 10);
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    const uniqueId = `${timestamp}_${randomStr}_${uuid}`;
    formData.append('unique_id', uniqueId);

    setIsAggregateLoading(true);
    setAggregateError(null);
    setSearchResults([]);
    setAggregateSearchStage("Preparing images for processing...");
    setAggregateSearchProgress(0);

    // Start progress simulation
    const progressInterval = simulateAggregateSearchProgress();

    try {
      const response = await fetch(`${API_URL}/api/search-aggregate`, {
        method: 'POST',
        body: formData,
      });

      // Clear the progress interval
      clearInterval(progressInterval);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (!data.matches) {
        throw new Error(data.detail || 'No matches found');
      }

      setSubmittedFaceUrl(data.submitted_face || null);
      setSearchResults(data.matches);
      setAggregateSearchStage("Aggregate search complete!");
      setAggregateSearchProgress(100);

      if (data.matches.length === 0) {
        toast({
          title: 'No Matches Found',
          description: 'No matching faces were found in the database.',
          status: 'info',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else {
        toast({
          title: 'Aggregate Search Complete',
          description: `Found ${data.matches.length} potential matches using ${selectedFiles.length} images`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (error: any) {
      clearInterval(progressInterval);
      setAggregateError(error.message || 'An error occurred during aggregate search');
      setAggregateSearchStage("Aggregate search failed");
      setAggregateSearchProgress(0);
      setSubmittedFaceUrl(null);

      toast({
        title: 'Error',
        description: error.message || 'An error occurred during aggregate search',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      // Keep isLoading true for a moment to show the completion message
      setTimeout(() => {
        setIsAggregateLoading(false);
      }, 1000);
    }
  };

  // Function to create a collage from multiple images
  const createImageCollage = async (files: File[]): Promise<Blob | null> => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      // Calculate grid dimensions based on number of images
      const numImages = files.length;
      const cols = Math.ceil(Math.sqrt(numImages));
      const rows = Math.ceil(numImages / cols);
      
      // Set canvas size (adjust as needed)
      const canvasWidth = 400;
      const canvasHeight = 300;
      const cellWidth = canvasWidth / cols;
      const cellHeight = canvasHeight / rows;
      
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;

      // Load and draw all images
      const imagePromises = files.map(file => {
        return new Promise<HTMLImageElement>((resolve, reject) => {
          const img = document.createElement('img') as HTMLImageElement;
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = URL.createObjectURL(file);
        });
      });

      const images = await Promise.all(imagePromises);

      // Draw images in grid
      images.forEach((img, index) => {
        const col = index % cols;
        const row = Math.floor(index / cols);
        
        const x = col * cellWidth;
        const y = row * cellHeight;
        
        // Calculate scaling to fit in cell while maintaining aspect ratio
        const scale = Math.min(cellWidth / img.width, cellHeight / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        
        // Center the image in the cell
        const centerX = x + (cellWidth - scaledWidth) / 2;
        const centerY = y + (cellHeight - scaledHeight) / 2;
        
        ctx.drawImage(img, centerX, centerY, scaledWidth, scaledHeight);
      });

      // Convert canvas to blob
      return new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            resolve(new Blob([], { type: 'image/jpeg' }));
          }
        }, 'image/jpeg', 0.8);
      });

    } catch (error) {
      console.error('Error creating collage:', error);
      return null;
    }
  };

  const handleFaceToFaceVerification = async () => {
    if (!firstImage || !secondImage) {
      setFaceToFaceError("Please select both images for comparison");
      return;
    }

    setIsFaceToFaceLoading(true);
    setFaceToFaceError(null);
    setSimilarityResult(null);
    setModelBreakdown([]);

    const formData = new FormData();
    formData.append('source_image', firstImage);
    formData.append('target_image', secondImage);

    try {
      const response = await fetch(`${API_URL}/api/face-compare`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();
      const similarity = data.similarity ?? data.result?.[0]?.face_matches?.[0]?.similarity;
      const breakdownFromApi = Array.isArray(data.model_breakdown) ? data.model_breakdown : [];
      const breakdownFallback = data.model_scores && typeof data.model_scores === 'object'
        ? Object.entries(data.model_scores).map(([model, score]) => ({
            model,
            score: Number(score),
          }))
        : [];
      const normalizedBreakdown: Array<{ model: string; score: number }> = (breakdownFromApi.length > 0 ? breakdownFromApi : breakdownFallback)
        .filter((entry: any) => entry && typeof entry.model === 'string' && Number.isFinite(Number(entry.score)))
        .map((entry: any) => ({ model: entry.model, score: Number(entry.score) }))
        .sort((a: { model: string; score: number }, b: { model: string; score: number }) => b.score - a.score);

      if (similarity !== undefined && similarity !== null) {
        setSimilarityResult(similarity);
        setModelBreakdown(normalizedBreakdown);
        toast({
          title: 'Verification Complete',
          description: `Similarity: ${(similarity * 100).toFixed(2)}%`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else {
        throw new Error('No similarity result found');
      }
    } catch (error: any) {
      console.error('Face-to-Face verification error:', error);
      setFaceToFaceError(error.message || 'Failed to compare images');
      toast({
        title: 'Error',
        description: error.message || 'Failed to compare images',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsFaceToFaceLoading(false);
    }
  };

  const handleEyesOnlyComparison = async () => {
    if (!firstImage || !secondImage) {
      setFaceToFaceError("Please select both images for comparison");
      return;
    }

    setIsEyesOnlyLoading(true);
    setFaceToFaceError(null);
    setEyesOnlySimilarityResult(null);
    setPeriocularFbrSimilarityResult(null);
    setEyesOnlyRawSimilarity(null);
    setEyesOnlyBaseline(null);
    setEyesOnlyModelBreakdown([]);
    setFirstMaskedEyesUrl(null);
    setSecondMaskedEyesUrl(null);
    setAlignedSourceUrl(null);
    setAlignedTargetUrl(null);
    setFbrSourceLeftUrl(null);
    setFbrSourceRightUrl(null);
    setFbrTargetLeftUrl(null);
    setFbrTargetRightUrl(null);
    setSourceEyeHoles([]);
    setTargetEyeHoles([]);
    setRoiSource(null);
    setRoiTarget(null);
    const formData = new FormData();
    formData.append('source_image', firstImage);
    formData.append('target_image', secondImage);

    try {
      const params = new URLSearchParams({ eyes_only: 'true' });
      if (useSkiMaskMode) {
        params.set('ski_mask_mode', 'true');
        if (sourceEyeHoles.length === 2) {
          formData.append('source_eye_holes', JSON.stringify(sourceEyeHoles));
        }
        if (targetEyeHoles.length === 2) {
          formData.append('target_eye_holes', JSON.stringify(targetEyeHoles));
        }
      } else {
        params.set('roi_w_scale', roiWScale.toFixed(3));
        params.set('roi_h_scale', roiHScale.toFixed(3));
        params.set('roi_x_offset', roiXOffset.toFixed(3));
        params.set('roi_y_offset', roiYOffset.toFixed(3));
      }
      const response = await fetch(`${API_URL}/api/face-compare?${params.toString()}`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error: ${response.status}`);
      }

      const data = await response.json();
      const similarity = data.eyes_only_similarity ?? data.similarity ?? data.result?.[0]?.face_matches?.[0]?.similarity;
      const eyesBreakdownFromApi = Array.isArray(data.model_breakdown) ? data.model_breakdown : [];
      const eyesBreakdownFallback = data.model_scores && typeof data.model_scores === 'object'
        ? Object.entries(data.model_scores).map(([model, score]) => ({ model, score: Number(score) }))
        : [];
      const normalizedEyesBreakdown: Array<{ model: string; score: number }> = (eyesBreakdownFromApi.length > 0 ? eyesBreakdownFromApi : eyesBreakdownFallback)
        .filter((entry: any) => entry && typeof entry.model === 'string' && Number.isFinite(Number(entry.score)))
        .map((entry: any) => ({ model: entry.model, score: Number(entry.score) }))
        .sort((a: { model: string; score: number }, b: { model: string; score: number }) => b.score - a.score);
      if (similarity !== undefined && similarity !== null) {
        setEyesOnlySimilarityResult(similarity);
        setEyesOnlyRawSimilarity(typeof data.eyes_only_raw_similarity === 'number' ? data.eyes_only_raw_similarity : null);
        setEyesOnlyBaseline(typeof data.eyes_only_baseline === 'number' ? data.eyes_only_baseline : null);
        setEyesOnlyModelBreakdown(normalizedEyesBreakdown);
        setFirstMaskedEyesUrl(typeof data.masked_source_image === 'string' ? data.masked_source_image : null);
        setSecondMaskedEyesUrl(typeof data.masked_target_image === 'string' ? data.masked_target_image : null);
        setAlignedSourceUrl(typeof data.aligned_source_image === 'string' ? data.aligned_source_image : null);
        setAlignedTargetUrl(typeof data.aligned_target_image === 'string' ? data.aligned_target_image : null);
        setSourceEyeHoles(Array.isArray(data.eye_holes_source) ? data.eye_holes_source : []);
        setTargetEyeHoles(Array.isArray(data.eye_holes_target) ? data.eye_holes_target : []);
        setRoiSource(data.roi_source && typeof data.roi_source === 'object' ? data.roi_source : null);
        setRoiTarget(data.roi_target && typeof data.roi_target === 'object' ? data.roi_target : null);
        toast({
          title: 'Eyes-Only Comparison Complete',
          description: `Eyes-only similarity: ${(similarity * 100).toFixed(2)}%`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else {
        throw new Error('No eyes-only similarity result found');
      }
    } catch (error: any) {
      console.error('Eyes-only comparison error:', error);
      setFaceToFaceError(error.message || 'Failed to compare eye regions');
      toast({
        title: 'Error',
        description: error.message || 'Failed to compare eye regions',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsEyesOnlyLoading(false);
    }
  };

  const handlePeriocularFbrComparison = async () => {
    if (!firstImage || !secondImage) {
      setFaceToFaceError("Please select both images for comparison");
      return;
    }
    setIsPeriocularFbrLoading(true);
    setFaceToFaceError(null);
    setPeriocularFbrSimilarityResult(null);
    setPeriocularFbrRawSimilarityResult(null);

    const formData = new FormData();
    formData.append('source_image', firstImage);
    formData.append('target_image', secondImage);

    const params = new URLSearchParams();
    if (useSkiMaskMode) {
      params.set('ski_mask_mode', 'true');
      if (sourceEyeHoles.length === 2) {
        formData.append('source_eye_holes', JSON.stringify(sourceEyeHoles));
      }
      if (targetEyeHoles.length === 2) {
        formData.append('target_eye_holes', JSON.stringify(targetEyeHoles));
      }
    }

    try {
      const response = await fetch(`${API_URL}/api/face-to-face/periocular-fbr?${params.toString()}`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Server error: ${response.status}`);
      }
      const data = await response.json();
      const cosine = typeof data.cosine === 'number' ? data.cosine : null;
      const rawCosine = typeof data.raw_cosine === 'number' ? data.raw_cosine : null;
      if (cosine === null) {
        throw new Error("No periocular cosine returned");
      }
      setPeriocularFbrSimilarityResult(cosine);
      setPeriocularFbrRawSimilarityResult(rawCosine);
      setAlignedSourceUrl(typeof data.aligned_source_image === 'string' ? data.aligned_source_image : null);
      setAlignedTargetUrl(typeof data.aligned_target_image === 'string' ? data.aligned_target_image : null);
      setSourceEyeHoles(Array.isArray(data.eye_holes_source) ? data.eye_holes_source : []);
      setTargetEyeHoles(Array.isArray(data.eye_holes_target) ? data.eye_holes_target : []);
      setFbrSourceLeftUrl(typeof data.eye_crops?.source_left === 'string' ? data.eye_crops.source_left : null);
      setFbrSourceRightUrl(typeof data.eye_crops?.source_right === 'string' ? data.eye_crops.source_right : null);
      setFbrTargetLeftUrl(typeof data.eye_crops?.target_left === 'string' ? data.eye_crops.target_left : null);
      setFbrTargetRightUrl(typeof data.eye_crops?.target_right === 'string' ? data.eye_crops.target_right : null);
      toast({
        title: 'Periocular (FBR) Complete',
        description: `Periocular (FBR) similarity: ${(cosine * 100).toFixed(2)}%`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } catch (error: any) {
      setFaceToFaceError(error.message || 'Failed periocular FBR comparison');
      toast({
        title: 'Error',
        description: error.message || 'Failed periocular FBR comparison',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsPeriocularFbrLoading(false);
    }
  };

  const handleSubmit = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Generate a more reliable unique identifier
    const timestamp = new Date().getTime();
    // Use a more reliable random string generation
    const randomStr = Math.random().toString(36).substring(2, 10);
    // Add a UUID-like component to ensure uniqueness
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    const uniqueId = `${timestamp}_${randomStr}_${uuid}`;
    formData.append('unique_id', uniqueId);

    setIsLoading(true);
    setError(null);
    setSearchResults([]);
    setSearchStage("Preparing image for processing...");
    setSearchProgress(0);

    // Start progress simulation
    const progressInterval = simulateSearchProgress();

    try {
      const response = await fetch(`${API_URL}/api/search`, {
        method: 'POST',
        body: formData,
      });

      // Clear the progress interval
      clearInterval(progressInterval);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (!data.matches) {
        throw new Error(data.detail || 'No matches found');
      }

      setSubmittedFaceUrl(data.submitted_face || null);
      setSearchResults(data.matches);
      setSearchStage("Search complete!");
      setSearchProgress(100);

      if (data.matches.length === 0) {
        toast({
          title: 'No Matches Found',
          description: 'No matching faces were found in the database.',
          status: 'info',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else {
        toast({
          title: 'Search Complete',
          description: `Found ${data.matches.length} potential matches`,
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (error: any) {
      clearInterval(progressInterval);
      setError(error.message || 'An error occurred during search');
      setSearchStage("Search failed");
      setSearchProgress(0);
      setSubmittedFaceUrl(null);

      toast({
        title: 'Error',
        description: error.message || 'An error occurred during search',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      // Keep isLoading true for a moment to show the completion message
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      // Clean up aggregate URLs
      previewUrls.forEach(url => {
        URL.revokeObjectURL(url);
      });
      // Clean up face-to-face URLs
      if (firstPreviewUrl) {
        URL.revokeObjectURL(firstPreviewUrl);
      }
      if (secondPreviewUrl) {
        URL.revokeObjectURL(secondPreviewUrl);
      }
    };
  }, [previewUrl, previewUrls, firstPreviewUrl, secondPreviewUrl]);

  return (
    <Box minH="100vh" w="100vw" bg="gray.900" color="white">
      <Box position="absolute" top={4} right={4}>
        <Button
          leftIcon={<FaHome />}
          colorScheme="blue"
          onClick={() => navigate('/')}
        >
          Home
        </Button>
      </Box>
      <Header />
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        w="full"
        pt="20px"
        pb="40px"
      >
        <Tabs variant="soft-rounded" colorScheme="blue" w="full" maxW="1600px" mx={4}>
          <TabList justifyContent="center" mb={8}>
            <Tab>Face Matching</Tab>
            <Tab>Feature Enhancement</Tab>
            <Tab>Aggregate Search</Tab>
            <Tab>Face-to-Face</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <VStack spacing={8} w="full" maxW="800px" mx="auto">
                {error && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}

                <Box
                  w="full"
                  h="400px"
                  border="3px dashed"
                  borderColor={isDragging ? "blue.400" : "gray.500"}
                  borderRadius="xl"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  bg={isDragging ? 'rgba(66, 153, 225, 0.1)' : 'transparent'}
                  transition="all 0.2s"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  position="relative"
                  _hover={{ borderColor: "blue.400" }}
                  p={8}
                >
                  <Input
                    type="file"
                    height="100%"
                    width="100%"
                    position="absolute"
                    top="0"
                    left="0"
                    opacity="0"
                    aria-hidden="true"
                    accept="image/*"
                    onChange={handleFileChange}
                  />

                  {previewUrl ? (
                    <Image
                      src={previewUrl}
                      alt="Preview"
                      maxH="220px"
                      objectFit="contain"
                      borderRadius="md"
                    />
                  ) : (
                    <InsightLogoPlaceholder isDragging={isDragging} />
                  )}

                  <Text fontSize="xl" mb={2} mt={4}>
                    {selectedFile ? selectedFile.name : 'Drag and drop an image here'}
                  </Text>
                  <Text fontSize="md" color="gray.500">
                    or click to select a file
                  </Text>
                </Box>

                {isLoading && (
                  <Box textAlign="center" w="full">
                    <Spinner size="xl" />
                    <Text mt={2} fontSize="lg" color="blue.300">{searchStage}</Text>
                    <Progress
                      value={searchProgress}
                      size="lg"
                      colorScheme="blue"
                      mt={4}
                      borderRadius="md"
                      w="full"
                    />
                    <Text mt={2} fontSize="sm" color="gray.500">
                      This may take a few moments depending on the complexity of the image.
                    </Text>
                  </Box>
                )}

                {searchResults.length > 0 && (
                  <SearchResults
                    searchResults={searchResults}
                    submittedFaceUrl={submittedFaceUrl}
                    API_URL={API_URL}
                  />
                )}
              </VStack>
            </TabPanel>

            <TabPanel>
              <FeatureEnhance API_URL={API_URL} />
            </TabPanel>

            <TabPanel>
              <VStack spacing={8} w="full" maxW="800px" mx="auto">
                {aggregateError && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    {aggregateError}
                  </Alert>
                )}

                <Box
                  w="full"
                  h="400px"
                  border="3px dashed"
                  borderColor={isAggregateDragging ? "blue.400" : "gray.500"}
                  borderRadius="xl"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  bg={isAggregateDragging ? 'rgba(66, 153, 225, 0.1)' : 'transparent'}
                  transition="all 0.2s"
                  onDrop={handleAggregateDrop}
                  onDragOver={handleAggregateDragOver}
                  onDragLeave={handleAggregateDragLeave}
                  position="relative"
                  _hover={{ borderColor: "blue.400" }}
                  p={8}
                >
                  <Input
                    type="file"
                    height="100%"
                    width="100%"
                    position="absolute"
                    top="0"
                    left="0"
                    opacity="0"
                    aria-hidden="true"
                    accept="image/*"
                    onChange={handleAggregateFileChange}
                    multiple
                  />

                  {previewUrls.length > 0 ? (
                    <SimpleGrid columns={3} spacing={4}>
                      {previewUrls.map((url, index) => (
                        <Box key={index} position="relative">
                          <Image
                            src={url}
                            alt={`Preview ${index + 1}`}
                            maxH="100px"
                            objectFit="contain"
                            borderRadius="md"
                          />
                          <IconButton
                            icon={<FaTimes />}
                            size="sm"
                            colorScheme="red"
                            onClick={() => removeFileFromAggregate(index)}
                            position="absolute"
                            top="2"
                            right="2"
                            borderRadius="full"
                            aria-label="Remove file"
                          />
                        </Box>
                      ))}
                    </SimpleGrid>
                  ) : (
                    <InsightLogoPlaceholder isDragging={isAggregateDragging} />
                  )}

                  <Text fontSize="xl" mb={2} mt={4}>
                    {selectedFiles.length > 0 ? `Selected ${selectedFiles.length} images` : 'Drag and drop multiple images here'}
                  </Text>
                  <Text fontSize="md" color="gray.500">
                    or click to select multiple files
                  </Text>
                </Box>

                {isAggregateLoading && (
                  <Box textAlign="center" w="full">
                    <Spinner size="xl" />
                    <Text mt={2} fontSize="lg" color="blue.300">{aggregateSearchStage}</Text>
                    <Progress
                      value={aggregateSearchProgress}
                      size="lg"
                      colorScheme="blue"
                      mt={4}
                      borderRadius="md"
                      w="full"
                    />
                    <Text mt={2} fontSize="sm" color="gray.500">
                      This may take a few moments depending on the number of images.
                    </Text>
                  </Box>
                )}

                {searchResults.length > 0 && (
                  <SearchResults
                    searchResults={searchResults}
                    submittedFaceUrl={submittedFaceUrl}
                    API_URL={API_URL}
                  />
                )}

                {selectedFiles.length > 0 && (
                  <HStack spacing={4} mt={4}>
                    <Button
                      colorScheme="blue"
                      onClick={handleAggregateSubmit}
                      isLoading={isAggregateLoading}
                      loadingText="Processing..."
                    >
                      Submit for Aggregate Search
                    </Button>
                    <Button
                      colorScheme="red"
                      onClick={() => {
                        setSelectedFiles([]);
                        setPreviewUrls([]);
                        setAggregateError(null);
                      }}
                    >
                      Clear Selected Files
                    </Button>
                  </HStack>
                )}
              </VStack>
            </TabPanel>

            <TabPanel>
              <VStack spacing={4} w="full" maxW="800px" mx="auto">
                <Text fontSize="xl" mb={4} textAlign="center">Face-to-Face Verification</Text>
                <Flex justify="center" gap={4}>
                  <Box
                    w="full"
                    h="300px"
                    border="3px dashed"
                    borderColor={isFirstDragging ? "blue.400" : "gray.500"}
                    borderRadius="xl"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    bg={isFirstDragging ? 'rgba(66, 153, 225, 0.1)' : 'transparent'}
                    transition="all 0.2s"
                    onDrop={(e) => handleFaceToFaceDrop(e, false)}
                    onDragOver={(e) => handleFaceToFaceDragOver(e, false)}
                    onDragLeave={(e) => handleFaceToFaceDragLeave(e, false)}
                    position="relative"
                    _hover={{ borderColor: "blue.400" }}
                    p={8}
                  >
                    <Input
                      type="file"
                      height="100%"
                      width="100%"
                      position="absolute"
                      top="0"
                      left="0"
                      opacity="0"
                      aria-hidden="true"
                      accept="image/*"
                      onChange={(e) => handleFaceToFaceFileChange(e, false)}
                    />
                    {firstPreviewUrl ? (
                      <Image
                        src={firstPreviewUrl}
                        alt="First Image Preview"
                        maxH="200px"
                        objectFit="contain"
                        borderRadius="md"
                      />
                    ) : (
                      <VStack spacing={4} align="center">
                        <SmallInsightLogoPlaceholder isDragging={isFirstDragging} />
                        <Text fontSize="md" color="gray.500" textAlign="center">
                          Drag and drop the first image here
                        </Text>
                      </VStack>
                    )}
                  </Box>
                  <Box
                    w="full"
                    h="300px"
                    border="3px dashed"
                    borderColor={isSecondDragging ? "blue.400" : "gray.500"}
                    borderRadius="xl"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    bg={isSecondDragging ? 'rgba(66, 153, 225, 0.1)' : 'transparent'}
                    transition="all 0.2s"
                    onDrop={(e) => handleFaceToFaceDrop(e, true)}
                    onDragOver={(e) => handleFaceToFaceDragOver(e, true)}
                    onDragLeave={(e) => handleFaceToFaceDragLeave(e, true)}
                    position="relative"
                    _hover={{ borderColor: "blue.400" }}
                    p={8}
                  >
                    <Input
                      type="file"
                      height="100%"
                      width="100%"
                      position="absolute"
                      top="0"
                      left="0"
                      opacity="0"
                      aria-hidden="true"
                      accept="image/*"
                      onChange={(e) => handleFaceToFaceFileChange(e, true)}
                    />
                    {secondPreviewUrl ? (
                      <Image
                        src={secondPreviewUrl}
                        alt="Second Image Preview"
                        maxH="200px"
                        objectFit="contain"
                        borderRadius="md"
                      />
                    ) : (
                      <VStack spacing={4} align="center">
                        <SmallInsightLogoPlaceholder isDragging={isSecondDragging} />
                        <Text fontSize="md" color="gray.500" textAlign="center">
                          Drag and drop the second image here
                        </Text>
                      </VStack>
                    )}
                  </Box>
                </Flex>
                {faceToFaceError && (
                  <Alert status="error" borderRadius="md" mt={4}>
                    <AlertIcon />
                    {faceToFaceError}
                  </Alert>
                )}
                <HStack w="full" justify="center" mt={1}>
                  <Text fontSize="sm" color="gray.300">Ski mask (eye holes)</Text>
                  <Switch
                    isChecked={useSkiMaskMode}
                    onChange={(e) => setUseSkiMaskMode(e.target.checked)}
                    colorScheme="teal"
                  />
                </HStack>
                <Card variant="outline" mt={2} p={3} w="full" maxW="760px">
                  <CardBody>
                    {!useSkiMaskMode ? (
                      <>
                        <Text fontSize="sm" color="gray.300" mb={3}>Eye ROI Controls (debug overlay)</Text>
                        <VStack spacing={3} align="stretch">
                          <Box>
                            <Text fontSize="xs" color="gray.400">Box Width (decrease ← → increase): {roiWScale.toFixed(2)}</Text>
                            <Slider min={0.6} max={1.8} step={0.01} value={roiWScale} onChange={setRoiWScale}>
                              <SliderTrack><SliderFilledTrack /></SliderTrack><SliderThumb />
                            </Slider>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.400">Box Height (decrease ← → increase): {roiHScale.toFixed(2)}</Text>
                            <Slider min={0.2} max={1.0} step={0.01} value={roiHScale} onChange={setRoiHScale}>
                              <SliderTrack><SliderFilledTrack /></SliderTrack><SliderThumb />
                            </Slider>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.400">Move Box Left/Right (left ← → right): {roiXOffset.toFixed(2)}</Text>
                            <Slider min={-0.5} max={0.5} step={0.01} value={roiXOffset} onChange={setRoiXOffset}>
                              <SliderTrack><SliderFilledTrack /></SliderTrack><SliderThumb />
                            </Slider>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.400">Move Box Up/Down (up ← → down): {roiYOffset.toFixed(2)}</Text>
                            <Slider min={-0.5} max={0.5} step={0.01} value={roiYOffset} onChange={setRoiYOffset}>
                              <SliderTrack><SliderFilledTrack /></SliderTrack><SliderThumb />
                            </Slider>
                          </Box>
                        </VStack>
                      </>
                    ) : (
                      <Text fontSize="xs" color="gray.400">
                        Direct edit mode: drag ellipse body to move; drag right/bottom handles to resize.
                        Run Eyes Only Comparison once to initialize default ellipses from eye landmarks.
                      </Text>
                    )}
                  </CardBody>
                </Card>
                {useSkiMaskMode && (alignedSourceUrl || alignedTargetUrl) && (
                  <Flex justify="center" gap={4} w="full" mt={2}>
                    <Box w="full" maxW="280px">
                      <Text fontSize="sm" color="gray.300" mb={2} textAlign="center">Aligned + Eye Holes (Image 1)</Text>
                      {alignedSourceUrl && sourceEyeHoles.length === 2 ? (
                        <Box display="flex" justifyContent="center">
                          <EllipseEditor
                            imageUrl={alignedSourceUrl}
                            ellipses={sourceEyeHoles}
                            onChange={setSourceEyeHoles}
                          />
                        </Box>
                      ) : (
                        <Box h="224px" borderRadius="md" border="1px solid" borderColor="gray.700" bg="gray.800" />
                      )}
                    </Box>
                    <Box w="full" maxW="280px">
                      <Text fontSize="sm" color="gray.300" mb={2} textAlign="center">Aligned + Eye Holes (Image 2)</Text>
                      {alignedTargetUrl && targetEyeHoles.length === 2 ? (
                        <Box display="flex" justifyContent="center">
                          <EllipseEditor
                            imageUrl={alignedTargetUrl}
                            ellipses={targetEyeHoles}
                            onChange={setTargetEyeHoles}
                          />
                        </Box>
                      ) : (
                        <Box h="224px" borderRadius="md" border="1px solid" borderColor="gray.700" bg="gray.800" />
                      )}
                    </Box>
                  </Flex>
                )}
                {!useSkiMaskMode && (alignedSourceUrl || alignedTargetUrl) && (
                  <Flex justify="center" gap={4} w="full" mt={2}>
                    <Box w="full" maxW="240px">
                      <Text fontSize="sm" color="gray.300" mb={2} textAlign="center">Aligned + ROI (Image 1)</Text>
                      <Box position="relative" w="112px" h="112px" mx="auto">
                        {alignedSourceUrl && (
                          <Image src={alignedSourceUrl} alt="Aligned Source" w="112px" h="112px" borderRadius="md" border="1px solid" borderColor="gray.600" />
                        )}
                        {roiSource && (
                          <Box
                            position="absolute"
                            left={`${roiSource.x1}px`}
                            top={`${roiSource.y1}px`}
                            width={`${Math.max(1, roiSource.x2 - roiSource.x1)}px`}
                            height={`${Math.max(1, roiSource.y2 - roiSource.y1)}px`}
                            border="2px solid"
                            borderColor="teal.300"
                            pointerEvents="none"
                          />
                        )}
                      </Box>
                    </Box>
                    <Box w="full" maxW="240px">
                      <Text fontSize="sm" color="gray.300" mb={2} textAlign="center">Aligned + ROI (Image 2)</Text>
                      <Box position="relative" w="112px" h="112px" mx="auto">
                        {alignedTargetUrl && (
                          <Image src={alignedTargetUrl} alt="Aligned Target" w="112px" h="112px" borderRadius="md" border="1px solid" borderColor="gray.600" />
                        )}
                        {roiTarget && (
                          <Box
                            position="absolute"
                            left={`${roiTarget.x1}px`}
                            top={`${roiTarget.y1}px`}
                            width={`${Math.max(1, roiTarget.x2 - roiTarget.x1)}px`}
                            height={`${Math.max(1, roiTarget.y2 - roiTarget.y1)}px`}
                            border="2px solid"
                            borderColor="teal.300"
                            pointerEvents="none"
                          />
                        )}
                      </Box>
                    </Box>
                  </Flex>
                )}
                {(firstMaskedEyesUrl || secondMaskedEyesUrl) && (
                  <Flex justify="center" gap={4} w="full" mt={2}>
                    <Box w="full" maxW="360px">
                      <Text fontSize="sm" color="gray.300" mb={2} textAlign="center">Masked Eyes (Image 1)</Text>
                      {firstMaskedEyesUrl ? (
                        <Image
                          src={firstMaskedEyesUrl}
                          alt="Masked Eyes Preview 1"
                          w="full"
                          maxH="160px"
                          objectFit="contain"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.600"
                          bg="black"
                        />
                      ) : (
                        <Box h="160px" borderRadius="md" border="1px solid" borderColor="gray.700" bg="gray.800" />
                      )}
                    </Box>
                    <Box w="full" maxW="360px">
                      <Text fontSize="sm" color="gray.300" mb={2} textAlign="center">Masked Eyes (Image 2)</Text>
                      {secondMaskedEyesUrl ? (
                        <Image
                          src={secondMaskedEyesUrl}
                          alt="Masked Eyes Preview 2"
                          w="full"
                          maxH="160px"
                          objectFit="contain"
                          borderRadius="md"
                          border="1px solid"
                          borderColor="gray.600"
                          bg="black"
                        />
                      ) : (
                        <Box h="160px" borderRadius="md" border="1px solid" borderColor="gray.700" bg="gray.800" />
                      )}
                    </Box>
                  </Flex>
                )}
                {(fbrSourceLeftUrl || fbrSourceRightUrl || fbrTargetLeftUrl || fbrTargetRightUrl) && (
                  <Flex justify="center" gap={4} w="full" mt={2} wrap="wrap">
                    <Box w="full" maxW="170px">
                      <Text fontSize="xs" color="gray.300" mb={1} textAlign="center">FBR Src Left</Text>
                      {fbrSourceLeftUrl ? <Image src={fbrSourceLeftUrl} alt="FBR source left eye" borderRadius="md" border="1px solid" borderColor="gray.600" /> : <Box h="90px" bg="gray.800" borderRadius="md" />}
                    </Box>
                    <Box w="full" maxW="170px">
                      <Text fontSize="xs" color="gray.300" mb={1} textAlign="center">FBR Src Right</Text>
                      {fbrSourceRightUrl ? <Image src={fbrSourceRightUrl} alt="FBR source right eye" borderRadius="md" border="1px solid" borderColor="gray.600" /> : <Box h="90px" bg="gray.800" borderRadius="md" />}
                    </Box>
                    <Box w="full" maxW="170px">
                      <Text fontSize="xs" color="gray.300" mb={1} textAlign="center">FBR Tgt Left</Text>
                      {fbrTargetLeftUrl ? <Image src={fbrTargetLeftUrl} alt="FBR target left eye" borderRadius="md" border="1px solid" borderColor="gray.600" /> : <Box h="90px" bg="gray.800" borderRadius="md" />}
                    </Box>
                    <Box w="full" maxW="170px">
                      <Text fontSize="xs" color="gray.300" mb={1} textAlign="center">FBR Tgt Right</Text>
                      {fbrTargetRightUrl ? <Image src={fbrTargetRightUrl} alt="FBR target right eye" borderRadius="md" border="1px solid" borderColor="gray.600" /> : <Box h="90px" bg="gray.800" borderRadius="md" />}
                    </Box>
                  </Flex>
                )}
                {similarityResult !== null && (
                  <Card variant="filled" mt={4} p={4} bg="blue.600" color="white" w="full" maxW="520px">
                    <CardBody>
                      <Text fontSize="lg" fontWeight="bold">Similarity: {(similarityResult * 100).toFixed(2)}%</Text>
                      {modelBreakdown.length > 0 && (
                        <VStack mt={3} spacing={1} align="stretch">
                          {modelBreakdown.map((entry) => (
                            <Flex key={entry.model} justify="space-between">
                              <Text fontSize="sm" textTransform="lowercase">{entry.model}</Text>
                              <Text fontSize="sm" fontWeight="semibold">{(entry.score * 100).toFixed(2)}%</Text>
                            </Flex>
                          ))}
                        </VStack>
                      )}
                    </CardBody>
                  </Card>
                )}
                {eyesOnlySimilarityResult !== null && (
                  <Card variant="filled" mt={2} p={4} bg="teal.600" color="white" w="full" maxW="520px">
                    <CardBody>
                      <Text fontSize="lg" fontWeight="bold">Eyes Only Similarity: {(eyesOnlySimilarityResult * 100).toFixed(2)}%</Text>
                      {eyesOnlyBaseline !== null && (
                        <Text fontSize="sm" mt={1}>Baseline: {(eyesOnlyBaseline * 100).toFixed(2)}%</Text>
                      )}
                      {eyesOnlyRawSimilarity !== null && (
                        <Text fontSize="sm">Eyes-Only Cosine: {(eyesOnlyRawSimilarity * 100).toFixed(2)}%</Text>
                      )}
                      {eyesOnlyModelBreakdown.length > 0 && (
                        <VStack mt={3} spacing={1} align="stretch">
                          {eyesOnlyModelBreakdown.map((entry) => (
                            <Flex key={`eyes-${entry.model}`} justify="space-between">
                              <Text fontSize="sm" textTransform="lowercase">{entry.model}</Text>
                              <Text fontSize="sm" fontWeight="semibold">{(entry.score * 100).toFixed(2)}%</Text>
                            </Flex>
                          ))}
                        </VStack>
                      )}
                    </CardBody>
                  </Card>
                )}
                {periocularFbrSimilarityResult !== null && (
                  <Card variant="filled" mt={2} p={4} bg="orange.600" color="white" w="full" maxW="520px">
                    <CardBody>
                      <Text fontSize="lg" fontWeight="bold">Periocular (FBR) Similarity: {(periocularFbrSimilarityResult * 100).toFixed(2)}%</Text>
                      {periocularFbrRawSimilarityResult !== null && (
                        <Text fontSize="sm">Raw FBR Cosine: {periocularFbrRawSimilarityResult.toFixed(4)}</Text>
                      )}
                    </CardBody>
                  </Card>
                )}
                {firstImage && secondImage && (
                  <HStack spacing={3} mt={4}>
                    <Button
                      colorScheme="blue"
                      onClick={handleFaceToFaceVerification}
                      isLoading={isFaceToFaceLoading}
                      loadingText="Comparing..."
                    >
                      Compare Images
                    </Button>
                    <Button
                      colorScheme="teal"
                      onClick={handleEyesOnlyComparison}
                      isLoading={isEyesOnlyLoading}
                      loadingText="Comparing Eyes..."
                    >
                      Eyes Only Comparison
                    </Button>
                  </HStack>
                )}
                {firstImage && secondImage && (
                  <Button
                    colorScheme="red"
                    onClick={() => {
                      setFirstImage(null);
                      setSecondImage(null);
                      setFirstPreviewUrl(null);
                      setSecondPreviewUrl(null);
                      setSimilarityResult(null);
                      setEyesOnlySimilarityResult(null);
                      setEyesOnlyRawSimilarity(null);
                      setEyesOnlyBaseline(null);
                      setEyesOnlyModelBreakdown([]);
                      setPeriocularFbrSimilarityResult(null);
                      setPeriocularFbrRawSimilarityResult(null);
                      setFirstMaskedEyesUrl(null);
                      setSecondMaskedEyesUrl(null);
                      setAlignedSourceUrl(null);
                      setAlignedTargetUrl(null);
                      setFbrSourceLeftUrl(null);
                      setFbrSourceRightUrl(null);
                      setFbrTargetLeftUrl(null);
                      setFbrTargetRightUrl(null);
                      setSourceEyeHoles([]);
                      setTargetEyeHoles([]);
                      setRoiSource(null);
                      setRoiTarget(null);
                      setModelBreakdown([]);
                      setFaceToFaceError(null);
                    }}
                    mt={4}
                  >
                    Clear Images
                  </Button>
                )}
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}

export default InSight;
